import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import ConnectionTestModal from './components/ConnectionTestModal';
import SecurityGuide from './components/SecurityGuide';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import amazonProductsService from '../../services/amazonProductsService';

const AmazonDatabaseConnection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Connection form state - Updated for real backend
  const [connectionData, setConnectionData] = useState({
    // Backend API Configuration
    backendUrl: 'https://mercadobulk.com',
    
    // Amazon PA-API Configuration
    amazonAccessKey: '',
    amazonSecretKey: '',
    amazonPartnerTag: '',
    amazonMarketplace: 'www.amazon.com',
    amazonRegion: 'us-east-1',
    
    // MySQL Database Configuration
    mysqlHost: '',
    mysqlUser: '',
    mysqlPassword: '',
    mysqlDatabase: 'productos_db',
    mysqlPort: '3306',
    mysqlSSL: true,
    
    // Search Configuration
    defaultCategory: 'All',
    maxProductsPerRequest: 50
  });

  // UI state
  const [showPasswordFields, setShowPasswordFields] = useState({
    amazonSecretKey: false,
    mysqlPassword: false
  });
  
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [backendStatus, setBackendStatus] = useState({
    connected: false,
    testing: false,
    lastTest: null
  });

  // Amazon marketplace options
  const amazonMarketplaces = [
    { value: 'www.amazon.com', label: 'Amazon.com (US)' },
    { value: 'www.amazon.co.uk', label: 'Amazon.co.uk (UK)' },
    { value: 'www.amazon.de', label: 'Amazon.de (Germany)' },
    { value: 'www.amazon.fr', label: 'Amazon.fr (France)' },
    { value: 'www.amazon.it', label: 'Amazon.it (Italy)' },
    { value: 'www.amazon.es', label: 'Amazon.es (Spain)' }
  ];

  // Product categories for Amazon search
  const amazonCategories = [
    { value: 'All', label: 'All Categories' },
    { value: 'Electronics', label: 'Electronics' },
    { value: 'Computers', label: 'Computers' },
    { value: 'Home', label: 'Home & Kitchen' },
    { value: 'Sports', label: 'Sports & Outdoors' },
    { value: 'Toys', label: 'Toys & Games' },
    { value: 'Automotive', label: 'Automotive' },
    { value: 'Books', label: 'Books' },
    { value: 'Clothing', label: 'Clothing' },
    { value: 'Beauty', label: 'Beauty & Personal Care' }
  ];

  // Enhanced form validation for real configuration
  const validateForm = () => {
    const errors = {};
    
    // Amazon PA-API validation
    if (!connectionData?.amazonAccessKey) {
      errors.amazonAccessKey = 'Amazon Access Key es requerido';
    }
    
    if (!connectionData?.amazonSecretKey) {
      errors.amazonSecretKey = 'Amazon Secret Key es requerido';
    }
    
    if (!connectionData?.amazonPartnerTag) {
      errors.amazonPartnerTag = 'Amazon Partner Tag es requerido';
    }
    
    // MySQL validation
    if (!connectionData?.mysqlHost) {
      errors.mysqlHost = 'MySQL Host es requerido';
    }
    
    if (!connectionData?.mysqlUser) {
      errors.mysqlUser = 'MySQL User es requerido';
    }
    
    if (!connectionData?.mysqlPassword) {
      errors.mysqlPassword = 'MySQL Password es requerido';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setConnectionData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear specific field error
    if (formErrors?.[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswordFields(prev => ({
      ...prev,
      [field]: !prev?.[field]
    }));
  };

  // Test backend connection
  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }

    setIsTestingConnection(true);
    setShowTestModal(true);
    setBackendStatus(prev => ({ ...prev, testing: true }));

    try {
      // Test backend API connection
      const result = await amazonProductsService?.testConnection();
      
      setConnectionResult({
        success: result?.success || false,
        message: result?.success 
          ? 'Conexión exitosa con backend y base de datos MySQL' : result?.message ||'Error al conectar con backend',
        details: {
          backend: result?.success ? 'Conectado' : 'Error',
          database: result?.data ? 'MySQL Activo' : 'No disponible',
          timestamp: result?.data?.server_time || new Date()?.toISOString(),
          apiUrl: amazonProductsService?.baseURL
        },
        error: result?.error
      });
      
      setBackendStatus({
        connected: result?.success || false,
        testing: false,
        lastTest: new Date()?.toISOString()
      });
      
    } catch (error) {
      setConnectionResult({
        success: false,
        message: 'Error al conectar con el servidor backend',
        error: error?.message || 'Error desconocido',
        details: {
          backend: 'Error',
          database: 'No disponible',
          timestamp: new Date()?.toISOString()
        }
      });
      
      setBackendStatus({
        connected: false,
        testing: false,
        lastTest: new Date()?.toISOString()
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Save configuration and proceed to data export
  const handleSaveAndContinue = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    
    try {
      // Save configuration to localStorage
      const configData = {
        ...connectionData,
        lastSaved: new Date()?.toISOString(),
        isConfigured: true
      };
      
      localStorage.setItem('amazonConnection', JSON.stringify(configData));

      // Show success message
      setConnectionResult({
        success: true,
        message: 'Configuración guardada exitosamente',
        details: {
          backend: 'Configurado',
          database: 'Configurado',
          amazon: 'PA-API Configurado'
        }
      });

      // Navigate to data export generator after a short delay
      setTimeout(() => {
        navigate('/data-export-generator');
      }, 1500);
      
    } catch (error) {
      console.error('Error saving configuration:', error);
      setFormErrors({ general: 'Error al guardar configuración' });
    } finally {
      setIsSaving(false);
    }
  };

  // Load existing configuration
  useEffect(() => {
    const savedConnection = localStorage.getItem('amazonConnection');
    if (savedConnection) {
      try {
        const parsedConnection = JSON.parse(savedConnection);
        setConnectionData(prev => ({
          ...prev,
          ...parsedConnection
        }));
      } catch (error) {
        console.error('Error loading saved connection:', error);
      }
    }
  }, []);

  // Test backend status on component mount
  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        const result = await amazonProductsService?.testConnection();
        setBackendStatus({
          connected: result?.success || false,
          testing: false,
          lastTest: new Date()?.toISOString()
        });
      } catch (error) {
        setBackendStatus({
          connected: false,
          testing: false,
          lastTest: new Date()?.toISOString()
        });
      }
    };

    checkBackendStatus();
  }, []);

  // Función para manejar la desconexión
  const handleDisconnect = () => {
    // Lógica para desconectar la API
    console.log('Desconectado de Amazon API');
    // Aquí puedes agregar la lógica para cerrar la conexión
  };

  // Updated fetchHistoryData to include headers and handle Supabase errors
  const fetchHistoryData = async () => {
    try {
      const response = await fetch(`${connectionData.backendUrl}/api/upload-history`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('History data fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('Error fetching history data:', error);
      return null;
    }
  };

  return (
    <>
    
      <Helmet>
        <title>Configuración Amazon Database - Sistema de Productos Amazon</title>
        <meta name="description" content="Configuración real de Amazon PA-API 5.0 y base de datos MySQL para descarga masiva de productos" />
      </Helmet>
      <div className="min-h-screen bg-background">
        
        
        <main className="pt-10 pb-8">
          <div className="max-w-4xl mx-auto px-6">
            {/* Header Section */}
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-4">
               
              </div>
              
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Database" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-text-primary">
                    Configuración Amazon PA-API 5.0
                  </h1>
                  <p className="text-text-secondary mt-1">
                    Configure su conexión real a Amazon Marketplace y base de datos MySQL
                  </p>
                </div>
              </div>

              {/* Backend Status */}
              <div className="bg-surface border border-border rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {backendStatus?.connected ? (
                      <Icon name="CheckCircle" size={20} className="text-success mr-2" />
                    ) : (
                      <Icon name="AlertCircle" size={20} className="text-warning mr-2" />
                    )}
                    <div>
                      <h4 className="font-semibold text-text-primary">
                        Estado del Backend: {backendStatus?.connected ? 'Conectado' : 'Desconectado'}
                      </h4>
                      <p className="text-sm text-text-secondary">
                        {backendStatus?.lastTest ? `Última prueba: ${new Date(backendStatus.lastTest)?.toLocaleString('es-ES')}` : 'Sin probar'}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {amazonProductsService?.baseURL}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Enhanced Configuration Form */}
              <div className="lg:col-span-2">
                <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
                  <h2 className="text-xl font-semibold text-text-primary mb-6">
                    Configuración Amazon PA-API 5.0
                  </h2>

                  <div className="space-y-6">
                    {/* Amazon PA-API Credentials */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-text-primary">
                        Credenciales Amazon Associate
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Access Key ID"
                          type="text"
                          value={connectionData?.amazonAccessKey}
                          onChange={(e) => handleInputChange('amazonAccessKey', e?.target?.value)}
                          placeholder="AKIA..."
                          error={formErrors?.amazonAccessKey}
                          required
                          // helperText eliminado
                        />
                        
                        <div className="relative">
                          <Input
                            label="Secret Access Key"
                            type={showPasswordFields?.amazonSecretKey ? "text" : "password"}
                            value={connectionData?.amazonSecretKey}
                            onChange={(e) => handleInputChange('amazonSecretKey', e?.target?.value)}
                            placeholder="••••••••••••••••"
                            error={formErrors?.amazonSecretKey}
                            required
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-8"
                            onClick={() => togglePasswordVisibility('amazonSecretKey')}
                          >
                            <Icon 
                              name={showPasswordFields?.amazonSecretKey ? "EyeOff" : "Eye"} 
                              size={16} 
                            />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Partner Tag (Associate ID)"
                          type="text"
                          value={connectionData?.amazonPartnerTag}
                          onChange={(e) => handleInputChange('amazonPartnerTag', e?.target?.value)}
                          placeholder="your-associate-tag-20"
                          error={formErrors?.amazonPartnerTag}
                          required
                          // helperText eliminado
                        />

                        <Select
                          label="Amazon Marketplace"
                          options={amazonMarketplaces}
                          value={connectionData?.amazonMarketplace}
                          onChange={(value) => handleInputChange('amazonMarketplace', value)}
                          required
                        />
                      </div>
                    </div>

                    {/* MySQL Database Configuration */}
                    <div className="space-y-4 pt-6 border-t border-border">
                      <h3 className="text-lg font-medium text-text-primary">
                        Base de Datos MySQL
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="MySQL Host"
                          type="text"
                          value={connectionData?.mysqlHost}
                          onChange={(e) => handleInputChange('mysqlHost', e?.target?.value)}
                          placeholder="mercadobulk.com o IP del servidor"
                          error={formErrors?.mysqlHost}
                          required
                          // helperText eliminado
                        />

                        <Input
                          label="Puerto MySQL"
                          type="number"
                          value={connectionData?.mysqlPort}
                          onChange={(e) => handleInputChange('mysqlPort', e?.target?.value)}
                          placeholder="3306"
                          // helperText eliminado
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Usuario MySQL"
                          type="text"
                          value={connectionData?.mysqlUser}
                          onChange={(e) => handleInputChange('mysqlUser', e?.target?.value)}
                          placeholder="root"
                          error={formErrors?.mysqlUser}
                          required
                        />
                        
                        <div className="relative">
                          <Input
                            label="Contraseña MySQL"
                            type={showPasswordFields?.mysqlPassword ? "text" : "password"}
                            value={connectionData?.mysqlPassword}
                            onChange={(e) => handleInputChange('mysqlPassword', e?.target?.value)}
                            placeholder="••••••••"
                            error={formErrors?.mysqlPassword}
                            required
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-8"
                            onClick={() => togglePasswordVisibility('mysqlPassword')}
                          >
                            <Icon 
                              name={showPasswordFields?.mysqlPassword ? "EyeOff" : "Eye"} 
                              size={16} 
                            />
                          </Button>
                        </div>
                      </div>

                      <Input
                        label="Nombre de Base de Datos"
                        type="text"
                        value={connectionData?.mysqlDatabase}
                        onChange={(e) => handleInputChange('mysqlDatabase', e?.target?.value)}
                        placeholder="productos_db"
                        // helperText eliminado
                      />
                    </div>

                    {/* Search Configuration */}
                    <div className="space-y-4 pt-6 border-t border-border">
                      <h3 className="text-lg font-medium text-text-primary">
                        Configuración de Búsqueda
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select
                          label="Categoría Predeterminada"
                          options={amazonCategories}
                          value={connectionData?.defaultCategory}
                          onChange={(value) => handleInputChange('defaultCategory', value)}
                        />
                        
                        <Input
                          label="Máximo Productos por Solicitud"
                          type="number"
                          min="1"
                          max="100"
                          value={connectionData?.maxProductsPerRequest}
                          onChange={(e) => handleInputChange('maxProductsPerRequest', parseInt(e?.target?.value) || 50)}
                          // helperText eliminado
                        />
                      </div>
                    </div>
                  </div>

                  {/* Show general errors */}
                  {formErrors?.general && (
                    <div className="mt-6 p-4 bg-error/10 border border-error/20 rounded-lg">
                      <div className="text-error text-sm">{formErrors?.general}</div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border">
                    <Button
                      variant="outline"
                      onClick={handleTestConnection}
                      disabled={isTestingConnection || backendStatus?.testing}
                      className="flex-1 sm:flex-none"
                    >
                      <Icon name="Zap" size={16} className="mr-2" />
                      {isTestingConnection ? 'Probando Backend...' : 'Probar Conexión'}
                    </Button>

                    <Button
                      onClick={handleSaveAndContinue}
                      disabled={isSaving}
                      className="flex-1"
                    >
                      <Icon name="Save" size={16} className="mr-2" />
                      {isSaving ? 'Guardando...' : 'Guardar y Continuar'}
                    </Button>
                  </div>

                  {/* Agregar un botón para desconectar la conexión con Amazon API */}
                  <button
                    onClick={() => handleDisconnect()}
                    style={{
                      marginTop: '10px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                    }}
                    onMouseOver={(e) => (e.target.style.backgroundColor = '#c82333')}
                    onMouseOut={(e) => (e.target.style.backgroundColor = '#dc3545')}
                  >
                    Desconectar
                  </button>
                </div>
              </div>

              {/* Enhanced Security Guide Sidebar */}
              <div className="lg:col-span-1">
                <SecurityGuide />
              </div>
            </div>
          </div>
        </main>

        {/* Enhanced Connection Test Modal */}
        {showTestModal && (
          <ConnectionTestModal
            isOpen={showTestModal}
            isLoading={isTestingConnection}
            result={connectionResult}
            onClose={() => setShowTestModal(false)}
            onRetry={handleTestConnection}
          />
        )}
      </div>
    </>
  );
};

export default AmazonDatabaseConnection;